/**
 * API module returned by optimizely2.get('dcp').
 *
 * @module
 */

var _ = require('optimizely-client-fn');

var PromiseEnv = require('core/lib/promise').Promise;

module.exports = [
  'stores/visitor_attribute_entity',
  'stores/visitor',
  'stores/provider_status',
  function(VisitorAttributeEntityStore, VisitorStore, ProviderStatusStore) {
    return {
      /**
       * Return the visitor's value (potentially undefined) for a particular VisitorAttribute,
       * or throw an error if the provided options are not enough to identify any single VisitorAttribute.
       *
       * For now, only DCPDataSourceAttributes (a subclass of VisitorAttributes) are supported.
       * Callers must specify options.datasourceId, along with options.attributeId xor options.attributeName.
       *
       * If we have cached data for the currently-set key in the specified datasource, AND an attribute value has been
       * uploaded for the currently-set key, then the return value will that attribute value.  Otherwise, it will be
       * undefined.
       *
       * Example usage:
       *   optimizely2.get('dcp').getAttributeValue({datasourceId: 123, attributeId: 456});        // -> 'bronze'
       *   optimizely2.get('dcp').getAttributeValue({datasourceId: 123, attributeName: 'Plan'});   // -> 'bronze'
       *
       * @param {VisitorAttributeDescriptor} options Specifies a particular VisitorAttribute entity.
       * @returns {*|undefined} The visitor's value for the specified attribute (possibly undefined).
       */
      getAttributeValue: _.partial(getAttributeValue, VisitorAttributeEntityStore, VisitorStore),

      /**
       * Return a Promise that resolves to the visitor's value (potentially undefined) for a particular VisitorAttribute,
       * or throw an error if the provided options are not enough to identify any single VisitorAttribute.
       *
       * For now, only DCPDataSourceAttributes (a subclass of VisitorAttributes) are supported.
       * Callers must specify options.datasourceId, along with options.attributeId xor options.attributeName.
       *
       * If we have cached data for the currently-set key in the specified datasource, then the returned Promise will
       * resolve quickly with that cached data.  Otherwise, it will resolve with fresh data after a delay.  In either
       * case, the attribute value will be undefined if no attribute value has been uploaded for the currently-set key.
       *
       * Example usage:
       *   optimizely2.get('dcp').waitForAttributeValue({datasourceId: 123, attributeId: 456}).then(function (attrVal) {
       *     attrVal; // -> 'bronze'
       *   });
       *   optimizely2.get('dcp').waitForAttributeValue({datasourceId: 123, attributeName: 'Plan'}).then(function (attrVal) {
       *     attrVal; // -> 'bronze'
       *   });
       *
       * @param {VisitorAttributeDescriptor} options Specifies a particular VisitorAttribute entity.
       * @returns {Promise} Resolves to the visitor's value for the specified attribute (possibly undefined).
       */
      waitForAttributeValue: _.partial(waitForAttributeValue, VisitorAttributeEntityStore, VisitorStore, ProviderStatusStore),
    };
  }
];

function getAttributeValue(VisitorAttributeEntityStore, VisitorStore, options) {
  var attribute = getDCPDataSourceAttribute(VisitorAttributeEntityStore, options);

  // May have been cleared by the DCP provider upon `activate`, if aliases have changed.
  var dcpData = VisitorStore.getAttribute('dcpData');
  if (dcpData) {
    return getAttributeValueFromDCPData(attribute, dcpData);
  }
}

function waitForAttributeValue(VisitorAttributeEntityStore, VisitorStore, ProviderStatusStore, options) {
  var attribute = getDCPDataSourceAttribute(VisitorAttributeEntityStore, options);

  // Return cached data, if we have any that's still valid.
  var dcpData = VisitorStore.getAttribute('dcpData');
  if (dcpData) {
    var attributeValue = getAttributeValueFromDCPData(attribute, dcpData);
    if (!_.isUndefined(attributeValue)) {
      return PromiseEnv.resolve(attributeValue);
    }
  }

  // Sure to be defined because the dcpData provider is currently eager, i.e. it's initialized by the controller.
  var dcpDataPromise = ProviderStatusStore.getPendingAttributeValue('dcpData');

  return dcpDataPromise.then(_.partial(getAttributeValueFromDCPData, attribute));
}

/**
 * @private
 * @param {Store} VisitorAttributeEntityStore
 * @param {VisitorAttributeDescriptor} options Specifies a particular VisitorAttribute entity.
 * @returns {VisitorAttribute}
 */
function getDCPDataSourceAttribute(VisitorAttributeEntityStore:any, options:VisitorAttributeDescriptor): VisitorAttribute {
  var attribute = VisitorAttributeEntityStore.getVisitorAttribute(options);
  if (!attribute.dcp_datasource_id) {
    throw new Error('Unrecognized DCP data source ID');
  }
  return attribute;
}

/**
 * @private
 * @param {VisitorAttribute} attribute
 * @param {DCPData} dcpData The visitor's profile data from the DCP backend.
 * @returns {*|undefined} The visitor's value for the specified attribute.
 */
function getAttributeValueFromDCPData(attribute:VisitorAttribute, dcpData:DCPData): any|undefined {
  var attributesData = dcpData.attributes || {};
  var datasourceData = attributesData[attribute.dcp_datasource_id] || {};
  return datasourceData[attribute.id];
}
